import React, { useEffect, useMemo, useState } from "react";
import {
  ActionKey,
  ActionLabel,
  editLabel,
  NewLabel,
} from "Components/constants/Role";
import TableContainer from "Components/Base/BaseTable";
import {
  ProformaKey,
  ProformaLabel,
} from "Components/constants/ProformaInvoice";
import BaseButton from "Components/Base/BaseButton";
import { checkStatusCodeSuccess, DownloadLabel, notFound, pdfDownload, rupeeSymbol, Search } from "Components/constants/Common";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RiSearchLine } from "react-icons/ri";
import { listOfProformaApi, proformaPdfApi } from "Api/ProformaInvoice";
import Loader from "Components/Base/BaseLoader";

const ProformaInvoice = () => {
  document.title = ProformaLabel.ProformaInvoiceTitle;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [proformaInvoice, setProformaInvoice] = useState<any[]>([]);
  const [searchProformaInvoice, setSearchProformaInvoice] = useState<Array<any>>([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loader, setLoader] = useState<boolean>(false);
  const [pageLoader, setPageLoader] = useState<boolean>(false);
  const navigate = useNavigate();

  const fetchProforma = async () => {
    const payload = {
      limit: pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfProformaApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setProformaInvoice(res?.data?.Proforma || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setProformaInvoice([]);
        }
      })
      .catch(() => {
        setProformaInvoice([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchProforma = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfProformaApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setSearchProformaInvoice(res?.data?.Proforma || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setSearchProformaInvoice([]);
        }
      })
      .catch(() => {
        setSearchProformaInvoice([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchProforma();
    } else {
      fetchProforma();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const handleDownload = (row:any) => {
    pdfDownload(
      proformaPdfApi,
      row?.id,
      `proforma_invoice_${row?.pro_in_no}.pdf`,
      setPageLoader
    );
  };
  
  const proformaInvoiceColumns = useMemo(
    () => [
      {
        header: ProformaLabel.VendorID,
        accessorKey: ProformaKey.VendorID,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ProformaLabel.VendorName,
        accessorKey: ProformaKey.VendorName,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ProformaLabel.ProformaInvoice,
        accessorKey: ProformaKey.ProformaInvoice,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ProformaLabel.ProformaInvoiceDT,
        accessorKey: ProformaKey.ProformaInvoiceDT,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ProformaLabel.TaxableValue,
        accessorKey: ProformaKey.TaxableValue,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.taxable);
        },
      },
      {
        header: ProformaLabel.GST,
        accessorKey: ProformaKey.GST,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.gst);
        },
      },
      {
        header: ProformaLabel.TaxAmt,
        accessorKey: ProformaKey.TaxInvoiceAmt,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.tax_invoice_amount);
        },
      },
      {
        header: ProformaLabel.Warranty,
        accessorKey: ProformaKey.Warranty,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ProformaLabel.Remarks,
        accessorKey: ProformaKey.Remarks,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <>
              <div
                className={
                  cell?.row?.original?.remarks?.length > 25
                    ? `text-dark tooltip-container`
                    : `text-dark`
                }
              >
                <span className="material-name">
                  {cell?.row?.original?.remarks}
                </span>
                <div className="d-flex align-items-end text-truncate no-whitespace">
                  {cell?.row?.original?.remarks?.length > 25 ? (
                    <span>
                      {cell?.row?.original?.remarks?.substring(0, 25)}...
                    </span>
                  ) : (
                    cell?.row?.original?.remarks
                  )}
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => {
          return (
            <div className="d-flex justify-content-start enable-action flex-nowrap gap-2 icon">
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-download-outline fs-4 text-primary cursor-pointer`}
                  onClick={() => handleDownload(cell?.row?.original)}
                ></i>
                <span className="tooltip-text">{DownloadLabel}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-pencil-outline fs-4 text-info cursor-pointer`}
                  onClick={() =>
                    navigate(
                      `/edit-proforma-invoice/${cell?.row?.original?.id}`
                    )
                  }
                ></i>
                <span className="tooltip-text">{editLabel}</span>
              </span>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const fetchData = (page: number) => {
    setCurrentPage(page);
  };

  const fetchSortingData = (
    page: number,
    id: string,
    order: string
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleSearch = (value: string) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  return (
    <div className="page-content">
      {pageLoader && <Loader/>}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">
            {ProformaLabel.ProformaInvoice}
          </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={()=> navigate('/add-proforma-invoice')}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4">
          {loader || proformaInvoice?.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={proformaInvoiceColumns}
              data={searchValue ? searchProformaInvoice : proformaInvoice || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearch}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={fetchData}
              fetchSortingData={fetchSortingData}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProformaInvoice;
